'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

interface Testimonial {
  id: number;
  name: string;
  location: string;
  image: string;
  alt: string;
  rating: number;
  text: string;
  service: string;
  date: string;
}

interface CommunityTestimonialsProps {
  testimonials: Testimonial[];
  areaName: string;
}

export default function CommunityTestimonials({ testimonials, areaName }: CommunityTestimonialsProps) {
  const [currentIndex, setCurrentIndex] = useState(0);

  const handlePrevious = () => {
    setCurrentIndex((prev) => (prev === 0 ? testimonials.length - 1 : prev - 1));
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev === testimonials.length - 1 ? 0 : prev + 1));
  };

  const currentTestimonial = testimonials[currentIndex];

  return (
    <div className="bg-card rounded-lg shadow-brand p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="font-headline font-bold text-xl text-primary mb-1">
            {areaName} Customer Reviews
          </h3>
          <p className="text-sm text-muted-foreground font-body">
            {testimonials.length} verified local testimonials
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={handlePrevious}
            className="p-2 rounded-md bg-background border border-border hover:bg-muted transition-brand"
            aria-label="Previous testimonial"
          >
            <Icon name="ChevronLeftIcon" size={20} className="text-foreground" />
          </button>
          <button
            onClick={handleNext}
            className="p-2 rounded-md bg-background border border-border hover:bg-muted transition-brand"
            aria-label="Next testimonial"
          >
            <Icon name="ChevronRightIcon" size={20} className="text-foreground" />
          </button>
        </div>
      </div>

      <div className="bg-background rounded-lg p-6 border border-border">
        <div className="flex items-start space-x-4 mb-4">
          <div className="w-16 h-16 rounded-full overflow-hidden flex-shrink-0">
            <AppImage
              src={currentTestimonial.image}
              alt={currentTestimonial.alt}
              className="w-full h-full object-cover"
            />
          </div>
          <div className="flex-1">
            <div className="flex items-center justify-between mb-2">
              <div>
                <h4 className="font-headline font-bold text-base text-primary">
                  {currentTestimonial.name}
                </h4>
                <p className="text-sm text-muted-foreground font-body">
                  {currentTestimonial.location}
                </p>
              </div>
              <div className="flex items-center space-x-1">
                {[...Array(5)].map((_, index) => (
                  <Icon
                    key={index}
                    name="StarIcon"
                    size={16}
                    variant={index < currentTestimonial.rating ? 'solid' : 'outline'}
                    className={index < currentTestimonial.rating ? 'text-warning' : 'text-muted'}
                  />
                ))}
              </div>
            </div>
            <div className="flex items-center space-x-3 text-xs text-muted-foreground font-body">
              <span className="flex items-center space-x-1">
                <Icon name="WrenchScrewdriverIcon" size={14} />
                <span>{currentTestimonial.service}</span>
              </span>
              <span>•</span>
              <span>{currentTestimonial.date}</span>
            </div>
          </div>
        </div>

        <p className="text-muted-foreground font-body text-sm leading-relaxed mb-4">
          "{currentTestimonial.text}"
        </p>

        <div className="flex items-center justify-between pt-4 border-t border-border">
          <div className="flex items-center space-x-2">
            <Icon name="CheckBadgeIcon" size={20} className="text-success" variant="solid" />
            <span className="text-xs text-muted-foreground font-body">Verified Customer</span>
          </div>
          <span className="text-xs text-muted-foreground font-body">
            {currentIndex + 1} of {testimonials.length}
          </span>
        </div>
      </div>
    </div>
  );
}